import React, { useEffect, useState } from "react";
import { Button, Row, Input } from "reactstrap";
import styles from "./styles.scss";
import { serviceCaller } from "../../../composition";
import withStyles from "isomorphic-style-loader/withStyles";
import { firebaseConnect } from "react-redux-firebase";
import { connect } from "react-redux";
import {
  connectToDonorPerfect,
  donorPerfectSyncNow,
  getDonorPerfectCodes,
  getDonorPerfectConfig,
  setDonorPerfectSyncing,
} from "../../../../utils/fetch";
import {
  ApiKeyInputStepProps,
  SegmentsSelectStepProps,
  Props,
  ReviewDataStepProps,
  ConnectionOverviewProps,
} from "./types";
import StyledStepper, { StepItem } from "../../../../toolkit/stepper";
import SegmentsPills from "../common/segmentsPills";
import ReviewCard from "../common/reviewCard";
import ProgressBar from "../common/progressBar";
import ApiKeyInput from "../common/apiKeyInput";
import { PulseLoader } from "react-spinners";
import {
  ICodeModel,
  IDonorPerfectConfig,
  IConnectResponse,
  IToggleSyncResponse,
  ISyncResult,
  DonorPerfectImportFilterMode,
  IDonorPerfectSyncConfig,
} from "../../../../types/services/DonorPerfectTypes";
import {
  buildExtraGiftFieldsPayload,
  createDefaultExtraGiftFields,
  EXTRA_GIFT_FIELD_LABELS,
  ExtraGiftFieldsState,
  isExtraGiftFieldsValid,
  hasExtraGiftFieldsSelection,
  mapConfigExtraGiftFields,
} from "./extraGiftFieldsSection";
import AutoSyncSettingsStep from "./autoSyncSettingsStep";
import ExtraGiftFieldsSettingsStep from "./extraGiftFieldsSettingsStep";
import ApiKeySettingsStep from "./apiKeySettingsStep";
import ImportFilterSettingsStep from "./importFilterSettingsStep";
import { maskApiKey, formatImportFilterModeLabel, formatImportFilterModeTooltip } from "./utils";
import { get } from "lodash";
import { Segment } from "../common/segmentsPills/types";
import SegmentedToggle from "../common/segmentedToggle";
import ReactTooltip from "react-tooltip";
import SettingsOutlined from "@mui/icons-material/SettingsOutlined";
import VpnKeyOutlined from "@mui/icons-material/VpnKeyOutlined";
import PeopleOutline from "@mui/icons-material/PeopleOutline";
import FlagOutlined from "@mui/icons-material/FlagOutlined";
import SyncOutlined from "@mui/icons-material/SyncOutlined";

const STEP_ADDITIONAL_SETTINGS = 4;
const STEP_REVIEW = 5;
const STEP_PROGRESS = 6;
const STEP_OVERVIEW = 7;
const STEP_AUTO_SYNC_SETTINGS = 8;
const STEP_EXTRA_GIFT_FIELDS_SETTINGS = 9;
const STEP_API_KEY_SETTINGS = 10;
const STEP_IMPORT_FILTER_SETTINGS = 11;

const buildDonorPerfectWizardSteps = (): StepItem[] => [
  { label: "Enter API Key", icon: <VpnKeyOutlined fontSize="small" /> },
  { label: "Select Donor Types", icon: <PeopleOutline fontSize="small" /> },
  { label: "Select Flags", icon: <FlagOutlined fontSize="small" /> },
  {
    label: "Additional settings",
    icon: <SettingsOutlined fontSize="small" />,
  },
  { label: "Start Syncing", icon: <SyncOutlined fontSize="small" /> },
];

const getWizardStepperIndex = (currentStep: number): number => {
  if (currentStep <= 3) {
    return currentStep - 1;
  }
  if (currentStep === STEP_ADDITIONAL_SETTINGS) {
    return 3;
  }
  if (currentStep === STEP_REVIEW) {
    return 4;
  }
  return 0;
};

const getNextWizardStep = (
  currentStep: number,
  supportsExtraGiftFields: boolean
): number => {
  if (currentStep === 3 && supportsExtraGiftFields) {
    return STEP_ADDITIONAL_SETTINGS;
  }
  return currentStep + 1;
};

const getPreviousWizardStep = (
  currentStep: number,
  supportsExtraGiftFields: boolean
): number => {
  if (currentStep === STEP_REVIEW) {
    return supportsExtraGiftFields ? STEP_ADDITIONAL_SETTINGS : 3;
  }
  return currentStep - 1;
};
const responseSupportsExtraGiftFields = (
  response: Record<string, unknown> | undefined
): boolean =>
  !!response &&
  (Object.prototype.hasOwnProperty.call(response, "extraGiftFields") ||
    Object.prototype.hasOwnProperty.call(response, "generalLedgers") ||
    Object.prototype.hasOwnProperty.call(response, "cachedGeneralLedgers"));

const parseSegments = (from: Array<ICodeModel>): Array<Segment> => {
  return from.map((segment: ICodeModel) => ({
    id: segment.code,
    name: segment.description,
  }));
};

const obtainCodes = (from: Array<Segment>): Array<string> => {
  return from.map((segment: Segment) => segment.id.toString());
};

const findSelectedSegments = (
  codes: Array<string>,
  segments: Array<Segment>
) => {
  return segments.filter(segment => codes.includes(segment.id.toString()));
};

const formatSegmentSelection = (segments: Array<Segment>): string =>
  segments.length > 0
    ? segments.map(segment => segment.name).join(", ")
    : "None selected";

const resolveImportFilterMode = (
  mode?: string
): DonorPerfectImportFilterMode =>
  mode === "union" ? "union" : "intersection";

const hasImportFilterSelection = (
  selectedTypes: Array<Segment>,
  selectedFlags: Array<Segment>
): boolean => selectedTypes.length > 0 && selectedFlags.length > 0;

const formatExtraGiftFieldValue = (setting: {
  enabled: boolean;
  code: string;
  label?: string;
}): string =>
  setting.label?.trim() || setting.code || "Not selected";

const renderExtraGiftFieldsSummary = (
  fields: ExtraGiftFieldsState
): React.ReactNode => {
  const enabledFields = (
    Object.keys(EXTRA_GIFT_FIELD_LABELS) as Array<keyof ExtraGiftFieldsState>
  )
    .filter(key => fields[key].enabled)
    .map(key => ({
      label: EXTRA_GIFT_FIELD_LABELS[key],
      setting: fields[key],
    }));

  if (enabledFields.length === 0) {
    return "Disabled";
  }

  return enabledFields.map((field, index) => (
    <React.Fragment key={field.label}>
      {index > 0 && " | "}
      <strong>{field.label}</strong>: {formatExtraGiftFieldValue(field.setting)}
    </React.Fragment>
  ));
};

const ConnectionOverview = ({
  apiKey,
  isLoading,
  syncResult,
  syncConfig,
  selectedTypes,
  selectedFlags,
  autoSync,
  onEditTypes,
  onEditFlags,
  onSyncNow,
  onEditAutoSync,
  extraGiftFieldsSummary,
  onEditExtraGiftFields,
  onEditApiKey,
  showExtraGiftFields,
  importFilterSummary,
  importFilterMode,
  onEditImportFilter,
}: ConnectionOverviewProps) => {
  const resolvedSyncTime =
    syncResult?.syncTime || syncConfig?.lastSyncEnd || undefined;
  const syncHadError =
    syncResult?.isError ||
    syncResult?.error ||
    syncConfig?.isMessageError ||
    false;
  const syncErrorMessage =
    syncResult?.fetchError ||
    syncResult?.error ||
    syncConfig?.lastSyncMessage ||
    "An error occurred. Please try again later. If the issue persists, reach out and we'll help you out";

  const renderSyncStatus = () => {
    const syncRunning =
      isLoading || (syncConfig?.runningSyncStart ?? 0) > 0;

    if (syncRunning) {
      return (
        <div className={`${styles.overviewSyncStatus} ${styles.inProgressColor}`}>
          <i className="fa fa-spinner fa-spin" /> Sync in progress.
        </div>
      );
    }

    if (resolvedSyncTime && !syncHadError) {
      return (
        <div className={`${styles.overviewSyncStatus} ${styles.successColor}`}>
          <i className="fa fa-check-circle" /> Last successful sync on{" "}
          {new Date(resolvedSyncTime).toLocaleString()}.
        </div>
      );
    }

    if (syncHadError && (resolvedSyncTime || syncResult?.error)) {
      return (
        <div className={`${styles.overviewSyncStatus} ${styles.errorColor}`}>
          <i className="fa fa-close" /> {syncErrorMessage}
          {resolvedSyncTime
            ? ` on ${new Date(resolvedSyncTime).toLocaleString()}.`
            : "."}
        </div>
      );
    }

    return (
      <div className={styles.overviewSyncStatus}>
        <i className="fa fa-info-circle" /> No syncs have been made yet.
      </div>
    );
  };

  return (
    <div>
      <p>
        Your DonorPerfect account is already connected to ViewSpark. If you want
        to manage your synced data, you can select the edit option below.
      </p>
      <p>
        Remember that you can always add a contact to the NO_VIEWSPARK flag if
        you want it to be skipped from the synchronization process.
      </p>
      {renderSyncStatus()}
      <ReviewCard
        title="DonorPerfect API Key"
        value={maskApiKey(apiKey)}
        onEdit={onEditApiKey}
      />
      <Input type="hidden" name="apiKey" value={apiKey} valid />
      <div className={styles.reviewCardRow}>
        <ReviewCard
          title="Donor Types"
          value={selectedTypes}
          onEdit={!autoSync && onEditTypes}
          showAll={false}
        />
        <ReviewCard
          title="Segment Flags"
          value={selectedFlags}
          onEdit={!autoSync && onEditFlags}
          showAll={false}
        />
      </div>
      <div className={styles.reviewCardRow}>
        {importFilterSummary ? (
          <ReviewCard
            title="Import filter"
            value={
              <>
                {importFilterSummary}{" "}
                <span data-tip data-for="tpImportFilterOverview">
                  <i className="fa fa-question-circle" />
                </span>
                <ReactTooltip
                  id="tpImportFilterOverview"
                  type="info"
                  place="bottom"
                  effect="float"
                >
                  {formatImportFilterModeTooltip(importFilterMode)}
                </ReactTooltip>
              </>
            }
            showAll={false}
            onEdit={!autoSync && onEditImportFilter}
          />
        ) : null}
        <ReviewCard
          title="Auto Sync"
          value={
            autoSync ? (
              <>
                Enabled{" "}
                <span data-tip data-for="tpAutoSyncOverview">
                  <i className="fa fa-question-circle" />
                </span>
                <ReactTooltip
                  id="tpAutoSyncOverview"
                  type="info"
                  place="bottom"
                  effect="float"
                >
                  We will sync your information between DonorPerfect and
                  ViewSpark every 30 minutes
                </ReactTooltip>
              </>
            ) : (
              "Disabled"
            )
          }
          onEdit={onEditAutoSync}
        />
      </div>
      {showExtraGiftFields && (
        <ReviewCard
          title="Extra Gift Fields"
          value={extraGiftFieldsSummary}
          onEdit={onEditExtraGiftFields}
          showAll={false}
        />
      )}
      <div className={styles.overviewFooter}>
        <Button
          className={`${styles.button} ${styles.overviewSyncButton}`}
          onClick={!isLoading ? onSyncNow : undefined}
          disabled={isLoading}
        >
          {isLoading ? (
            <PulseLoader color="#1F65B1" loading={true} />
          ) : (
            "Sync now"
          )}
        </Button>
      </div>
    </div>
  );
};

const ReviewDataStep = ({
  apiKey,
  selectedFlags,
  selectedTypes,
  enableAutoSync,
  isConnected,
  isAutoSyncEnabled,
  onEditFlags,
  onEditTypes,
  setEnableAutoSync,
  importFilterMode,
  setImportFilterMode,
  extraGiftFields,
  showExtraGiftFields,
  onEditExtraGiftFields,
}: ReviewDataStepProps) => {
  const segmentNames = formatSegmentSelection(selectedFlags);
  const typeNames = formatSegmentSelection(selectedTypes);
  const showImportFilterMode = hasImportFilterSelection(
    selectedTypes,
    selectedFlags
  );
  const extraGiftFieldsSummary =
    extraGiftFields && showExtraGiftFields
      ? renderExtraGiftFieldsSummary(extraGiftFields)
      : null;
  const showExtraGiftFieldsSummary =
    !!extraGiftFields &&
    showExtraGiftFields &&
    hasExtraGiftFieldsSelection(extraGiftFields);

  const autoSyncToggle = (
    <>
      <SegmentedToggle
        title="Enable Auto Sync"
        value={enableAutoSync ? "on" : "off"}
        options={[
          { value: "on", label: "On" },
          { value: "off", label: "Off" },
        ]}
        onChange={value => setEnableAutoSync(value === "on")}
        showInfo={true}
        infoId="EnableAutoSync"
        centered={true}
        className={styles.reviewToggleItem}
      />
      <ReactTooltip
        id="tpEnableAutoSync"
        type="info"
        place="bottom"
        effect="float"
      >
        Select to automatically sync your DonorPerfect data every 30 minutes.
      </ReactTooltip>
    </>
  );

  return (
    <div>
      <p>
        {isConnected
          ? `Review your settings below and click "Update Settings" to proceed.`
          : `Review your selections below and click "Start Sync" to begin importing your donor data from DonorPerfect to ViewSpark.`}
      </p>
      <ReviewCard title="DonorPerfect API Key" value={maskApiKey(apiKey)} />
      <div className={styles.reviewCardRow}>
        <ReviewCard
          title="Donor Types"
          value={typeNames}
          onEdit={(!isConnected || !isAutoSyncEnabled) && onEditTypes}
          showAll={false}
        />
        <ReviewCard
          title="Segment Flags"
          value={segmentNames}
          onEdit={(!isConnected || !isAutoSyncEnabled) && onEditFlags}
          showAll={false}
        />
      </div>
      {showExtraGiftFieldsSummary && extraGiftFieldsSummary ? (
        <ReviewCard
          title="Extra Gift Fields"
          value={extraGiftFieldsSummary}
          onEdit={onEditExtraGiftFields}
          showAll={false}
        />
      ) : null}
      {showImportFilterMode ? (
        <div className={styles.reviewToggleRow}>
          <div className={styles.reviewToggleColumn}>
            <SegmentedToggle
              title="Import filter"
              value={importFilterMode}
              options={[
                { value: "intersection", label: "AND" },
                { value: "union", label: "OR" },
              ]}
              onChange={value =>
                setImportFilterMode(value as DonorPerfectImportFilterMode)
              }
              showInfo={true}
              infoId="ImportFilterMode"
              centered={true}
              className={styles.reviewToggleItem}
            />
            <ReactTooltip
              id="tpImportFilterMode"
              type="info"
              place="bottom"
              effect="float"
            >
              AND: import donors that match both a selected segment flag and a
              selected donor type. AND will import only donors that match.
              <br />
              <br />
              OR: import donors that match any selected flag or any selected donor
              type. OR will import all donors.
            </ReactTooltip>
          </div>
          <div className={styles.reviewToggleDivider} aria-hidden="true" />
          <div className={styles.reviewToggleColumn}>{autoSyncToggle}</div>
        </div>
      ) : (
        <div className={styles.reviewToggleRowSingle}>{autoSyncToggle}</div>
      )}
    </div>
  );
};

const SegmentsSelectStep = ({
  segments,
  allowEdit,
  selectedSegments,
  description,
  title,
  setSelectedSegments,
  setCanAdvance,
  requireSelection = true,
  validateAdvance,
}: SegmentsSelectStepProps) => {
  useEffect(() => {
    let nextCanAdvance = false;
    if (validateAdvance) {
      nextCanAdvance = validateAdvance();
    } else if (!requireSelection) {
      nextCanAdvance = true;
    } else {
      nextCanAdvance = selectedSegments.length > 0;
    }
    setCanAdvance(nextCanAdvance);
  }, [selectedSegments, requireSelection, setCanAdvance, validateAdvance]);

  return (
    <div>
      <p>{description}</p>
      <p className={styles.stepSectionHeading}>{title}</p>
      <SegmentsPills
        segments={segments}
        selectedSegments={selectedSegments}
        setSelectedSegments={setSelectedSegments}
        allowEdit={allowEdit}
      />
    </div>
  );
};

const ApiKeyInputStep = ({
  isConnected,
  currentApiKey,
  charityId,
  integrationsServiceUrl,
  serviceAccessToken,
  setApiKey,
  setFlags,
  setTypes,
  setIsConnected,
  setCanAdvance,
  setGeneralLedgers,
  setCampaigns,
  setThankYouLetters,
  onCodesLoaded,
}: ApiKeyInputStepProps) => {
  const [apiKeyValue, setApiKeyValue] = useState<string>(currentApiKey);
  const [isValid, setIsValid] = useState<boolean>(
    currentApiKey && isConnected ? true : undefined
  );
  const [canConnect, setCanConnect] = useState<boolean>(!!currentApiKey);
  const [feedback, setFeedback] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const connectKey = () => {
    setIsLoading(true);
    getDonorPerfectCodes(
      integrationsServiceUrl,
      charityId,
      apiKeyValue,
      serviceAccessToken
    )
      .then(response => {
        if (response.donorTypes && response.flags) {
          onCodesLoaded?.(response);
          const flags = parseSegments(response.flags);
          const types = parseSegments(response.donorTypes);
          setApiKey(apiKeyValue);
          setIsValid(true);
          setCanConnect(false);
          setIsConnected(true);
          setCanAdvance(true);
          setIsLoading(false);
          setFlags(flags);
          setTypes(types);
          setGeneralLedgers(response.generalLedgers || []);
          setCampaigns(response.campaigns || []);
          setThankYouLetters(response.thankYouLetters || []);
        } else {
          setIsValid(false);
          setIsLoading(false);
          setFeedback(
            response?.message || "An error ocurred. Try again later."
          );
        }
      })
      .catch(err => {
        setIsValid(false);
        setIsLoading(false);
        setFeedback(err.message);
      });
  };

  return (
    <div>
      <p>
        Enter your DonorPerfect API Key below to connect to your account. If you
        don't have an API Key, please refer to the DonorPerfect documentation on
        how to generate one.
      </p>
      <p className={styles.wizardHeading}>DonorPerfect API Key</p>
      <ApiKeyInput
        feedback={feedback}
        isValid={isValid}
        isConnected={isConnected}
        isLoading={isLoading}
        apiKeyValue={apiKeyValue}
        canConnect={canConnect}
        setApiKeyValue={setApiKeyValue}
        setCanConnect={setCanConnect}
        setIsValid={setIsValid}
        setFeedback={setFeedback}
        onConnectKey={connectKey}
      />
    </div>
  );
};

const DonorPerfect = (props: Props) => {
  const syncInProgress = props.syncInProgress ?? false;
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [config, setConfig] = useState<IDonorPerfectConfig>(undefined);
  const [canAdvance, setCanAdvance] = useState<boolean>(false);
  const [apiKey, setApiKey] = useState<string>();
  const [flags, setFlags] = useState<Array<Segment>>([]);
  const [selectedFlags, setSelectedFlags] = useState<Array<Segment>>([]);
  const [types, setTypes] = useState<Array<Segment>>([]);
  const [selectedTypes, setSelectedTypes] = useState<Array<Segment>>([]);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [isSyncing, setIsSyncing] = useState<boolean>(false);
  const [errorRequestingSync, setErrorRequestingSync] = useState<string>(
    undefined
  );
  const [enableAutoSync, setEnableAutoSync] = useState<boolean>(true);
  const [importFilterMode, setImportFilterMode] =
    useState<DonorPerfectImportFilterMode>("intersection");
  const [syncResult, setSyncResult] = useState<ISyncResult>(null);
  const [generalLedgers, setGeneralLedgers] = useState<ICodeModel[]>([]);
  const [campaigns, setCampaigns] = useState<ICodeModel[]>([]);
  const [thankYouLetters, setThankYouLetters] = useState<ICodeModel[]>([]);
  const [extraGiftFields, setExtraGiftFields] = useState<ExtraGiftFieldsState>(
    createDefaultExtraGiftFields()
  );
  const [supportsExtraGiftFields, setSupportsExtraGiftFields] =
    useState<boolean>(false);
  const applyCachedExtraGiftCodesFromConfig = (
    configResponse: IDonorPerfectConfig
  ) => {
    if (configResponse.cachedGeneralLedgers?.length) {
      setGeneralLedgers(configResponse.cachedGeneralLedgers);
    }
    if (configResponse.cachedCampaigns?.length) {
      setCampaigns(configResponse.cachedCampaigns);
    }
    if (configResponse.cachedThankYouLetters?.length) {
      setThankYouLetters(configResponse.cachedThankYouLetters);
    }
  };

  const buildConnectConfig = (): IDonorPerfectConfig => {
    const payload: IDonorPerfectConfig = {
      apiKey: apiKey,
      importedFlags: obtainCodes(selectedFlags),
      importedDonorTypes: obtainCodes(selectedTypes),
    };
    if (supportsExtraGiftFields) {
      payload.extraGiftFields = buildExtraGiftFieldsPayload(extraGiftFields);
    }
    if (hasImportFilterSelection(selectedTypes, selectedFlags)) {
      payload.importFilterMode = importFilterMode;
    }
    return payload;
  };

  const reconcileExtraGiftFields = (
    fields: ExtraGiftFieldsState,
    ledgerOptions: ICodeModel[],
    campaignOptions: ICodeModel[],
    thankYouLetterOptions: ICodeModel[]
  ): ExtraGiftFieldsState => {
    const reconcileField = (
      setting: ExtraGiftFieldsState["generalLedger"],
      options: ICodeModel[]
    ) => {
      if (!setting.enabled) {
        return setting;
      }
      if (options.some(option => option.code === setting.code)) {
        return setting;
      }
      return { enabled: false, code: "", label: "" };
    };
    return {
      generalLedger: reconcileField(fields.generalLedger, ledgerOptions),
      campaign: reconcileField(fields.campaign, campaignOptions),
      thankYouLetter: reconcileField(
        fields.thankYouLetter,
        thankYouLetterOptions
      ),
    };
  };

  const applyVerifiedApiKey = (
    payload: {
      apiKey: string;
      flags: Segment[];
      types: Segment[];
      generalLedgers: ICodeModel[];
      campaigns: ICodeModel[];
      thankYouLetters: ICodeModel[];
    },
    verifiedSupportsExtraGiftFields: boolean
  ) => {
    setSupportsExtraGiftFields(verifiedSupportsExtraGiftFields);
    setApiKey(payload.apiKey);
    setFlags(payload.flags);
    setTypes(payload.types);
    setGeneralLedgers(payload.generalLedgers);
    setCampaigns(payload.campaigns);
    setThankYouLetters(payload.thankYouLetters);
    setSelectedFlags(
      findSelectedSegments(obtainCodes(selectedFlags), payload.flags)
    );
    setSelectedTypes(
      findSelectedSegments(obtainCodes(selectedTypes), payload.types)
    );
    setExtraGiftFields(current =>
      reconcileExtraGiftFields(
        current,
        payload.generalLedgers,
        payload.campaigns,
        payload.thankYouLetters
      )
    );
  };

  useEffect(() => {
    if (!config) {
      getDonorPerfectConfig(
        props.config.integrationsServiceUrl,
        props.currentCharity.id,
        props.serviceAccessToken
      )
        .then(configResponse => {
          setSupportsExtraGiftFields(
            responseSupportsExtraGiftFields(configResponse)
          );
          setExtraGiftFields(
            mapConfigExtraGiftFields(configResponse?.extraGiftFields)
          );
          applyCachedExtraGiftCodesFromConfig(configResponse);
          if (configResponse?.connected) {
            return getDonorPerfectCodes(
              props.config.integrationsServiceUrl,
              props.currentCharity.id,
              configResponse?.apiKey,
              props.serviceAccessToken
            )
              .then(response => {
                setSupportsExtraGiftFields(
                  responseSupportsExtraGiftFields(response) ||
                    responseSupportsExtraGiftFields(configResponse)
                );
                const flags = parseSegments(response.flags);
                const types = parseSegments(response.donorTypes);

                setFlags(flags);
                setTypes(types);
                if (!configResponse.cachedGeneralLedgers?.length) {
                  setGeneralLedgers(response.generalLedgers || []);
                }
                if (!configResponse.cachedCampaigns?.length) {
                  setCampaigns(response.campaigns || []);
                }
                if (!configResponse.cachedThankYouLetters?.length) {
                  setThankYouLetters(response.thankYouLetters || []);
                }

                setSelectedFlags(
                  findSelectedSegments(configResponse?.importedFlags, flags)
                );
                setSelectedTypes(
                  findSelectedSegments(
                    configResponse?.importedDonorTypes,
                    types
                  )
                );

                setIsConnected(true);
                setCurrentStep(STEP_OVERVIEW);

                setConfig(configResponse);
                setApiKey(configResponse?.apiKey);
                setEnableAutoSync(configResponse?.syncing);
                setImportFilterMode(
                  resolveImportFilterMode(configResponse?.importFilterMode)
                );
              })
              .catch(error => {
                console.error(error);
                throw new Error("Error fetching flags and donor types");
              });
          } else if (configResponse.error) {
            throw new Error(`${configResponse}`);
          } else {
            setIsConnected(false);

            setConfig(configResponse);

            if (configResponse?.apiKey) {
              setApiKey(configResponse?.apiKey);
            }

            if (configResponse?.syncing) {
              setEnableAutoSync(configResponse?.syncing);
            }
            if (configResponse?.importFilterMode) {
              setImportFilterMode(
                resolveImportFilterMode(configResponse.importFilterMode)
              );
            }
          }
        })
        .catch(error => {
          setIsConnected(true);
        setCurrentStep(STEP_OVERVIEW);
          setConfig({
            apiKey: "Error",
            importedFlags: [],
            importedDonorTypes: [],
            connected: true,
            syncing: true,
          });
          setSyncResult({
            fetchError:
              "Something went wrong fetching your data. Please contact support",
            isError: true,
            syncTime: undefined,
          });
          console.error("Something very bad happened - ", error);
        });
    }
  }, [config]);

  useEffect(() => {
    if (apiKey === "" || flags.length === 0) return;

    setCanAdvance(true);
  }, [apiKey, flags]);

  useEffect(() => {
    if (
      currentStep === STEP_ADDITIONAL_SETTINGS ||
      currentStep === STEP_EXTRA_GIFT_FIELDS_SETTINGS
    ) {
      setCanAdvance(isExtraGiftFieldsValid(extraGiftFields));
    }
  }, [currentStep, extraGiftFields]);

  const updateStep = (step: number) => {
    setCurrentStep(step);
    setCanAdvance(false);
    if (
      step === STEP_REVIEW ||
      step === STEP_AUTO_SYNC_SETTINGS ||
      step === STEP_API_KEY_SETTINGS ||
      step === STEP_IMPORT_FILTER_SETTINGS
    ) {
      setCanAdvance(true);
    }
    if (
      step === STEP_ADDITIONAL_SETTINGS ||
      step === STEP_EXTRA_GIFT_FIELDS_SETTINGS
    ) {
      setCanAdvance(isExtraGiftFieldsValid(extraGiftFields));
    }
  };

  const goBack = () => {
    if (currentStep === 1 || currentStep === STEP_OVERVIEW) {
      props.onBack();
      return;
    }
    if (
      currentStep === STEP_AUTO_SYNC_SETTINGS ||
      currentStep === STEP_EXTRA_GIFT_FIELDS_SETTINGS ||
      currentStep === STEP_API_KEY_SETTINGS ||
      currentStep === STEP_IMPORT_FILTER_SETTINGS
    ) {
      updateStep(STEP_OVERVIEW);
      return;
    }
    if (!config?.connected) {
      setCurrentStep(
        getPreviousWizardStep(currentStep, supportsExtraGiftFields)
      );
    } else {
      updateStep(STEP_OVERVIEW);
    }
  };

  useEffect(() => {
    const syncConfig = props.syncConfig;
    if (!syncConfig) {
      return;
    }
    if ((syncConfig.runningSyncStart ?? 0) > 0) {
      return;
    }
    if (syncConfig.lastSyncEnd) {
      setSyncResult({
        message: syncConfig.lastSyncMessage,
        isError: syncConfig.isMessageError,
        syncTime: syncConfig.lastSyncEnd,
      });
    }
  }, [props.syncConfig]);

  useEffect(() => {
    if (currentStep !== STEP_PROGRESS || syncInProgress) {
      return;
    }
    setConfig(undefined);
  }, [currentStep, syncInProgress]);

  const syncNow = () => {
    donorPerfectSyncNow(
      props.config.integrationsServiceUrl,
      props.currentCharity.id,
      props.serviceAccessToken
    )
      .then(response => {
        if (response.isError || response.error) {
          setSyncResult({
            message: response.message,
            error: response.error,
            isError: true,
            syncTime: response.syncTime ?? Date.now(),
          });
          return;
        }
        if (response.message === "A sync is already running.") {
          alert(response.message);
          return;
        }
        alert("Synchronization requested.");
      })
      .catch(err => {
        setSyncResult({
          error: err.message,
          isError: true,
          syncTime: Date.now(),
        });
      });
  };

  useEffect(() => {
    setSelectedFlags(flags);
  }, [flags]);

  useEffect(() => {
    setSelectedTypes(types);
  }, [types]);

  const saveAutoSyncSettings = async () => {
    setIsSyncing(true);
    setCanAdvance(false);
    setErrorRequestingSync(undefined);

    return setDonorPerfectSyncing(
      props.config.integrationsServiceUrl,
      props.currentCharity.id,
      { syncing: enableAutoSync },
      props.serviceAccessToken
    )
      .then((response: IToggleSyncResponse) => {
        if (response.message === "Syncing setting changed successfully.") {
          return connectToDonorPerfect(
            props.config.integrationsServiceUrl,
            props.currentCharity.id,
            buildConnectConfig(),
            props.serviceAccessToken
          ).then((response: IConnectResponse) => {
            if (response.success) {
              return Promise.resolve();
            } else {
              throw new Error(response.message);
            }
          });
        } else {
          throw new Error(response.message);
        }
      })
      .then(() => {
        setConfig(undefined);
        setIsSyncing(false);
        updateStep(STEP_OVERVIEW);
      })
      .catch(err => {
        setIsSyncing(false);
        setCanAdvance(true);
        setErrorRequestingSync(
          "An error ocurred updating your preferences. Please try again later."
        );
        setCurrentStep(STEP_AUTO_SYNC_SETTINGS);
        console.error(err);
      });
  };

  const saveApiKeySettings = async () => {
    setIsSyncing(true);
    setCanAdvance(false);
    setErrorRequestingSync(undefined);

    return connectToDonorPerfect(
      props.config.integrationsServiceUrl,
      props.currentCharity.id,
      buildConnectConfig(),
      props.serviceAccessToken
    )
      .then((response: IConnectResponse) => {
        if (response.success) {
          return Promise.resolve();
        } else {
          throw new Error(response.message);
        }
      })
      .then(() => {
        setConfig(undefined);
        setIsSyncing(false);
        updateStep(STEP_OVERVIEW);
      })
      .catch(err => {
        setIsSyncing(false);
        setCanAdvance(true);
        setErrorRequestingSync(
          "An error ocurred updating your API key. Please try again later."
        );
        setCurrentStep(STEP_API_KEY_SETTINGS);
        console.error(err);
      });
  };

  const saveExtraGiftFieldsSettings = async () => {
    setIsSyncing(true);
    setCanAdvance(false);
    setErrorRequestingSync(undefined);

    return connectToDonorPerfect(
      props.config.integrationsServiceUrl,
      props.currentCharity.id,
      buildConnectConfig(),
      props.serviceAccessToken
    )
      .then((response: IConnectResponse) => {
        if (response.success) {
          return Promise.resolve();
        } else {
          throw new Error(response.message);
        }
      })
      .then(() => {
        setConfig(undefined);
        setIsSyncing(false);
        updateStep(STEP_OVERVIEW);
      })
      .catch(err => {
        setIsSyncing(false);
        setCanAdvance(true);
        setErrorRequestingSync(
          "An error ocurred updating your preferences. Please try again later."
        );
        setCurrentStep(STEP_EXTRA_GIFT_FIELDS_SETTINGS);
        console.error(err);
      });
  };

  const saveImportFilterSettings = async () => {
    setIsSyncing(true);
    setCanAdvance(false);
    setErrorRequestingSync(undefined);

    return connectToDonorPerfect(
      props.config.integrationsServiceUrl,
      props.currentCharity.id,
      buildConnectConfig(),
      props.serviceAccessToken
    )
      .then((response: IConnectResponse) => {
        if (response.success) {
          return Promise.resolve();
        }
        throw new Error(response.message);
      })
      .then(() => {
        setConfig(undefined);
        setIsSyncing(false);
        updateStep(STEP_OVERVIEW);
      })
      .catch(err => {
        setIsSyncing(false);
        setCanAdvance(true);
        setErrorRequestingSync(
          "An error ocurred updating your preferences. Please try again later."
        );
        setCurrentStep(STEP_IMPORT_FILTER_SETTINGS);
        console.error(err);
      });
  };

  const startSync = async (shouldSyncNow: boolean) => {
    setIsSyncing(true);
    setCanAdvance(false);
    setErrorRequestingSync(undefined);

    return connectToDonorPerfect(
      props.config.integrationsServiceUrl,
      props.currentCharity.id,
      buildConnectConfig(),
      props.serviceAccessToken
    )
      .then((response: IConnectResponse) => {
        if (response.success) {
          return setDonorPerfectSyncing(
            props.config.integrationsServiceUrl,
            props.currentCharity.id,
            { syncing: enableAutoSync },
            props.serviceAccessToken
          ).then((response: IToggleSyncResponse) => {
            if (response.message === "Syncing setting changed successfully.") {
              return Promise.resolve();
            } else {
              throw new Error(response.message);
            }
          });
        } else {
          throw new Error(response.message);
        }
      })
      .then(() => {
        if (!shouldSyncNow) {
          setConfig(undefined);
          setIsSyncing(false);
          return;
        }

        const charityId = props.currentCharity.id;
        return donorPerfectSyncNow(
          props.config.integrationsServiceUrl,
          charityId,
          props.serviceAccessToken
        ).then(response => {
          if (response.isError || response.error) {
            throw new Error(response.error || response.message);
          }
          setCurrentStep(STEP_PROGRESS);
          setIsSyncing(false);
        });
      })
      .catch(err => {
        setIsSyncing(false);
        setCanAdvance(true);
        setErrorRequestingSync(
          "There was an error connecting your account. Try again later."
        );
        setCurrentStep(STEP_REVIEW);
        console.error(err);
      });
  };

  const onClickNext = () => {
    if (currentStep === STEP_REVIEW) {
      if (selectedFlags.length === 0 && selectedTypes.length === 0) {
        setErrorRequestingSync(
          "Select at least one donor type or segment flag to import."
        );
        return;
      }
      startSync(!config.connected);
    } else {
      setCurrentStep(getNextWizardStep(currentStep, supportsExtraGiftFields));
    }
  };

  const onSaveSettings = () => {
    if (currentStep === STEP_AUTO_SYNC_SETTINGS) {
      saveAutoSyncSettings();
    } else if (currentStep === STEP_EXTRA_GIFT_FIELDS_SETTINGS) {
      saveExtraGiftFieldsSettings();
    } else if (currentStep === STEP_API_KEY_SETTINGS) {
      saveApiKeySettings();
    } else if (currentStep === STEP_IMPORT_FILTER_SETTINGS) {
      saveImportFilterSettings();
    }
  };

  const getSettingsScreenTitle = (): string => {
    if (currentStep === STEP_OVERVIEW && isConnected) {
      return "Your DonorPerfect sync";
    }
    if (currentStep === STEP_AUTO_SYNC_SETTINGS) {
      return "Auto Sync Settings";
    }
    if (currentStep === STEP_EXTRA_GIFT_FIELDS_SETTINGS) {
      return "Extra Gift Fields Settings";
    }
    if (currentStep === STEP_API_KEY_SETTINGS) {
      return "API Key Settings";
    }
    if (currentStep === STEP_IMPORT_FILTER_SETTINGS) {
      return "Import Filter Settings";
    }
    return "Connect DonorPerfect to ViewSpark";
  };

  const showWizardProgress =
    currentStep < STEP_OVERVIEW &&
    currentStep !== STEP_AUTO_SYNC_SETTINGS &&
    currentStep !== STEP_EXTRA_GIFT_FIELDS_SETTINGS &&
    currentStep !== STEP_API_KEY_SETTINGS &&
    currentStep !== STEP_IMPORT_FILTER_SETTINGS;

  const showDonorPerfectStepper =
    showWizardProgress && !!config && currentStep !== STEP_PROGRESS;

  const donorPerfectWizardSteps = buildDonorPerfectWizardSteps();

  const donorPerfectActiveStep = getWizardStepperIndex(currentStep);

  useEffect(() => {
    if (currentStep === 1 && isConnected && apiKey) {
      setCanAdvance(true);
    }
  }, [currentStep, isConnected, apiKey]);

  useEffect(() => {
    if (errorRequestingSync) setErrorRequestingSync(undefined);
  }, [currentStep]);

  useEffect(() => {
    if (
      (currentStep !== STEP_ADDITIONAL_SETTINGS &&
        currentStep !== STEP_EXTRA_GIFT_FIELDS_SETTINGS) ||
      !apiKey ||
      (generalLedgers.length > 0 &&
        campaigns.length > 0 &&
        thankYouLetters.length > 0)
    ) {
      return;
    }

    getDonorPerfectCodes(
      props.config.integrationsServiceUrl,
      props.currentCharity.id,
      apiKey,
      props.serviceAccessToken
    )
      .then(response => {
        if (response.generalLedgers) {
          setGeneralLedgers(response.generalLedgers);
        }
        if (response.campaigns) {
          setCampaigns(response.campaigns);
        }
        if (response.thankYouLetters) {
          setThankYouLetters(response.thankYouLetters);
        }
      })
      .catch(error => {
        console.error("Error fetching extra gift field codes", error);
      });
  }, [currentStep, apiKey, generalLedgers.length, campaigns.length, thankYouLetters.length]);

  return (
    <main>
      <div>
        <div>
          <h1 className={styles.h1}>{getSettingsScreenTitle()}</h1>
          {showWizardProgress && currentStep !== STEP_ADDITIONAL_SETTINGS && (
            <p className={styles.subtitle}>
              If you wish to prevent certain donors from syncing, add the
              NO_VIEWSPARK flag to their account.
            </p>
          )}
          {showDonorPerfectStepper && (
            <div className={styles.stepperBar}>
              <StyledStepper
                steps={donorPerfectWizardSteps}
                activeStep={donorPerfectActiveStep}
                onStepChange={() => {}}
              />
            </div>
          )}
          {!config || config === undefined ? (
            <div
              className={styles.stepContainer}
              style={{ textAlign: "center", margin: "100px 0px" }}
            >
              <PulseLoader color="#1F65B1" loading={true} />
            </div>
          ) : (
            <div className={styles.stepContainer}>
              {currentStep === 1 && (
                <ApiKeyInputStep
                  currentApiKey={config.apiKey || apiKey}
                  isConnected={isConnected}
                  charityId={props.currentCharity.id}
                  integrationsServiceUrl={props.config.integrationsServiceUrl}
                  serviceAccessToken={props.serviceAccessToken}
                  setApiKey={setApiKey}
                  setIsConnected={setIsConnected}
                  setCanAdvance={setCanAdvance}
                  setFlags={setFlags}
                  setTypes={setTypes}
                  setGeneralLedgers={setGeneralLedgers}
                  setCampaigns={setCampaigns}
                  setThankYouLetters={setThankYouLetters}
                  onCodesLoaded={response =>
                    setSupportsExtraGiftFields(
                      responseSupportsExtraGiftFields(response)
                    )
                  }
                />
              )}
              {currentStep === 2 && (
                <SegmentsSelectStep
                  segments={types}
                  allowEdit={true}
                  selectedSegments={selectedTypes}
                  description="Select the types of donors you want to import into ViewSpark. You can skip this step if you are importing by segment flags only."
                  title="Select donor to import as segments"
                  setSelectedSegments={setSelectedTypes}
                  setCanAdvance={setCanAdvance}
                  requireSelection={false}
                />
              )}
              {currentStep === 3 && (
                <SegmentsSelectStep
                  segments={flags}
                  allowEdit={true}
                  selectedSegments={selectedFlags}
                  description="Select flags to import as segments. You can skip this step if you are importing by donor types only. At least one donor type or flag is required overall."
                  title="Select segment flags for donors"
                  setSelectedSegments={setSelectedFlags}
                  setCanAdvance={setCanAdvance}
                  requireSelection={false}
                  validateAdvance={() =>
                    selectedTypes.length > 0 || selectedFlags.length > 0
                  }
                />
              )}
              {currentStep === STEP_ADDITIONAL_SETTINGS &&
                supportsExtraGiftFields && (
                  <ExtraGiftFieldsSettingsStep
                    extraGiftFields={extraGiftFields}
                    setExtraGiftFields={setExtraGiftFields}
                    generalLedgers={generalLedgers}
                    campaigns={campaigns}
                    thankYouLetters={thankYouLetters}
                    onValidationChange={setCanAdvance}
                  />
                )}
              {currentStep === STEP_REVIEW && (
                <div>
                  <ReviewDataStep
                    apiKey={apiKey}
                    selectedFlags={selectedFlags}
                    selectedTypes={selectedTypes}
                    enableAutoSync={enableAutoSync}
                    isConnected={config.connected}
                    isAutoSyncEnabled={config.syncing}
                    setEnableAutoSync={setEnableAutoSync}
                    importFilterMode={importFilterMode}
                    setImportFilterMode={setImportFilterMode}
                    onEditFlags={goBack}
                    onEditTypes={() => updateStep(2)}
                    extraGiftFields={extraGiftFields}
                    showExtraGiftFields={supportsExtraGiftFields}
                    onEditExtraGiftFields={() =>
                      updateStep(STEP_ADDITIONAL_SETTINGS)
                    }
                  />
                  {errorRequestingSync && (
                    <span className={styles.syncFeedback}>
                      <i className={`fa fa-close`} /> {errorRequestingSync}
                    </span>
                  )}
                </div>
              )}
              {currentStep === STEP_PROGRESS && <ProgressBar />}
              {currentStep === STEP_OVERVIEW && (
                <ConnectionOverview
                  apiKey={apiKey}
                  isLoading={syncInProgress}
                  syncResult={syncResult}
                  syncConfig={props.syncConfig}
                  selectedFlags={formatSegmentSelection(selectedFlags)}
                  selectedTypes={formatSegmentSelection(selectedTypes)}
                  importFilterSummary={
                    hasImportFilterSelection(selectedTypes, selectedFlags)
                      ? formatImportFilterModeLabel(importFilterMode)
                      : undefined
                  }
                  importFilterMode={
                    hasImportFilterSelection(selectedTypes, selectedFlags)
                      ? importFilterMode
                      : undefined
                  }
                  autoSync={enableAutoSync}
                  onEditAutoSync={() => updateStep(STEP_AUTO_SYNC_SETTINGS)}
                  onEditImportFilter={() =>
                    updateStep(STEP_IMPORT_FILTER_SETTINGS)
                  }
                  onEditTypes={() => updateStep(2)}
                  onEditFlags={() => updateStep(3)}
                  onSyncNow={syncNow}
                  extraGiftFieldsSummary={renderExtraGiftFieldsSummary(
                    extraGiftFields
                  )}
                  showExtraGiftFields={supportsExtraGiftFields}
                  onEditExtraGiftFields={
                    supportsExtraGiftFields
                      ? () => updateStep(STEP_EXTRA_GIFT_FIELDS_SETTINGS)
                      : undefined
                  }
                  onEditApiKey={
                    !enableAutoSync
                      ? () => updateStep(STEP_API_KEY_SETTINGS)
                      : undefined
                  }
                />
              )}
              {currentStep === STEP_API_KEY_SETTINGS && (
                <div>
                  <ApiKeySettingsStep
                    currentApiKey={apiKey}
                    charityId={props.currentCharity.id}
                    integrationsServiceUrl={
                      props.config.integrationsServiceUrl
                    }
                    serviceAccessToken={props.serviceAccessToken}
                    onApiKeyReady={applyVerifiedApiKey}
                    onValidationChange={setCanAdvance}
                  />
                  {errorRequestingSync && (
                    <span className={styles.syncFeedback}>
                      <i className={`fa fa-close`} /> {errorRequestingSync}
                    </span>
                  )}
                </div>
              )}
              {currentStep === STEP_AUTO_SYNC_SETTINGS && (
                <div>
                  <AutoSyncSettingsStep
                    enableAutoSync={enableAutoSync}
                    setEnableAutoSync={setEnableAutoSync}
                  />
                  {errorRequestingSync && (
                    <span className={styles.syncFeedback}>
                      <i className={`fa fa-close`} /> {errorRequestingSync}
                    </span>
                  )}
                </div>
              )}
              {currentStep === STEP_IMPORT_FILTER_SETTINGS && (
                <div>
                  <ImportFilterSettingsStep
                    importFilterMode={importFilterMode}
                    setImportFilterMode={setImportFilterMode}
                  />
                  {errorRequestingSync && (
                    <span className={styles.syncFeedback}>
                      <i className={`fa fa-close`} /> {errorRequestingSync}
                    </span>
                  )}
                </div>
              )}
              {currentStep === STEP_EXTRA_GIFT_FIELDS_SETTINGS && (
                <div>
                  <ExtraGiftFieldsSettingsStep
                    extraGiftFields={extraGiftFields}
                    setExtraGiftFields={setExtraGiftFields}
                    generalLedgers={generalLedgers}
                    campaigns={campaigns}
                    thankYouLetters={thankYouLetters}
                    onValidationChange={setCanAdvance}
                    showOptionalHint={false}
                  />
                  {errorRequestingSync && (
                    <span className={styles.syncFeedback}>
                      <i className={`fa fa-close`} /> {errorRequestingSync}
                    </span>
                  )}
                </div>
              )}

              {currentStep !== STEP_OVERVIEW && (
              <Row className={styles.actionsContainer}>
                <Button
                  className={styles.button}
                  onClick={goBack}
                  disabled={currentStep === STEP_PROGRESS}
                >
                  {currentStep === STEP_PROGRESS ? "Stop" : "Back"}
                </Button>
                {currentStep < STEP_PROGRESS && (
                  <Button
                    className={`${styles.button} ${
                      canAdvance ? styles.nextButton : null
                    }`}
                    onClick={onClickNext}
                    disabled={!canAdvance || isSyncing || syncInProgress}
                  >
                    {currentStep === STEP_REVIEW ? (
                      !isSyncing && !syncInProgress ? (
                        "Start Sync"
                      ) : (
                        <PulseLoader color="#fff" loading={true} />
                      )
                    ) : (
                      "Next"
                    )}
                  </Button>
                )}
                {(currentStep === STEP_AUTO_SYNC_SETTINGS ||
                  currentStep === STEP_EXTRA_GIFT_FIELDS_SETTINGS ||
                  currentStep === STEP_API_KEY_SETTINGS ||
                  currentStep === STEP_IMPORT_FILTER_SETTINGS) && (
                  <Button
                    className={`${styles.button} ${
                      canAdvance ? styles.nextButton : null
                    }`}
                    onClick={onSaveSettings}
                    disabled={!canAdvance || isSyncing}
                  >
                    {isSyncing ? (
                      <PulseLoader color="#fff" loading={true} />
                    ) : (
                      "Save Settings"
                    )}
                  </Button>
                )}
              </Row>
              )}
            </div>
          )}
        </div>
      </div>
    </main>
  );
};

export default serviceCaller(
  connect((state: { firebase: object; user: { currentCharity: { id: string } } }) => {
    const charityId = state.user.currentCharity?.id;
    const syncConfig = get(
      state.firebase,
      `data.${charityId}.internal.integrations.donorPerfectConfig.syncConfig`,
      null
    );
    return {
      syncConfig,
      syncInProgress: (syncConfig?.runningSyncStart ?? 0) > 0,
    };
  })(
    firebaseConnect((_, store) => {
      const charityId = store.getState().user.currentCharity?.id;
      if (!charityId) {
        return [];
      }
      return [`${charityId}/internal/integrations/donorPerfectConfig/syncConfig`];
    })(withStyles(styles)(DonorPerfect))
  )
);
